// 登录/注册/重置密码 三页共享的画布外壳:≥lg 复刻 1920×1080 设计稿(缩放+超宽屏灌入),<lg 流式 fallback;
// 右侧表单由各页经 renderDesktopForm/renderMobileForm 传入。
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { Theme, ThemeColors, THEME, THEME_ORDER, STAGE_W, STAGE_H } from './login-theme';
import Welcome from './welcome';
import { CompetitionRulesDialog } from './competition-rules-dialog';
import MobileLandscapeAuthShell from './mobile-landscape-auth-shell';
import { useIsMobileLandscapeStage } from '@/hooks/use-mobile';
// 橙色四色轮盘：走打包导入(带 hash)而非 public 绝对路径,避免部署环境取不到静态资源时破图
import themeWheel from '@/assets/images/theme-wheel.svg';

interface AuthCanvasProps {
  // 桌面端右侧表单：在右侧容器（width=STAGE_W=1920）内自行 absolute 定位（约定 left:1272, width:421）。
  renderDesktopForm: (ctx: { theme: Theme; colors: ThemeColors }) => React.ReactNode;
  // 移动端流式表单（最大宽度 420 居中）。
  renderMobileForm: (ctx: { theme: Theme; colors: ThemeColors }) => React.ReactNode;
  // 手机横屏(844×390)右侧表单列：在共享舞台壳内自行 absolute 定位(约定 left:524, width:203)。
  renderLandscapeForm?: (ctx: { theme: Theme; colors: ThemeColors }) => React.ReactNode;
  // 初始主题（默认 orange）。主题状态由本组件持有，切换影响 Welcome 配色 + 页面底色 + 顶部胶囊色。
  initialTheme?: Theme;
}

// 语言切换：胶囊轨道 + 白色滑块。
function LangToggle({ chip, variant = 'desktop' }: { chip: string; variant?: 'desktop' | 'mobile' }) {
  const { i18n } = useTranslation();
  const current = Cookies.get('i18next') || i18n.language || 'zh-HK';
  const isEn = current.startsWith('en');
  // changeLanguage 不写 cookie(LanguageDetector 已停用),需手动同步,否则旧 cookie 覆盖切换。
  const setLang = (lng: 'en' | 'zh-HK') => { i18n.changeLanguage(lng); Cookies.set('i18next', lng); };

  const seg = (active: boolean): React.CSSProperties => ({
    height: 31, padding: '0 12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: 28.5, background: active ? '#FFFFFF' : 'transparent',
    color: '#000', fontSize: 14, fontWeight: active ? 500 : 400, cursor: 'pointer', border: 0,
  });

  // 1920 设计稿：胶囊容器锁定 139×39（padding 4 + gap 4 + 两个 segment 自适应）
  const container: React.CSSProperties = variant === 'mobile'
    ? { height: 39, display: 'flex', alignItems: 'center', gap: 4, padding: 4, borderRadius: 28.5, background: chip }
    : { position: 'absolute', left: 1725, top: 28, width: 139, height: 39, display: 'flex', alignItems: 'center', gap: 4, padding: 4, borderRadius: 28.5, background: chip };

  return (
    <div style={container}>
      <button type="button" onClick={() => setLang('en')} style={seg(isEn)}>English</button>
      <button type="button" onClick={() => setLang('zh-HK')} style={seg(!isEn)}>繁體</button>
    </div>
  );
}

// 主题切换器：当前主题在最左 + 竖线分隔 + 其余三色圆点。
function ThemeSwitcher({ theme, onChange }: { theme: Theme; onChange: (t: Theme) => void }) {
  const others = THEME_ORDER.filter((x) => x !== theme);
  const swatch = (th: Theme, size: number) =>
    th === 'orange' ? (
      <img src={themeWheel} alt="" style={{ width: size, height: size, display: 'block' }} />
    ) : (
      <span style={{ width: size, height: size, borderRadius: '50%', background: THEME[th].dot, display: 'block' }} />
    );

  return (
    <div style={{ position: 'absolute', left: 1505, top: 28, height: 39, display: 'flex', alignItems: 'center', gap: 13, padding: '0 12px', borderRadius: 28.5, background: '#FFFFFF' }}>
      <button type="button" aria-label={`current theme ${theme}`} style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: 0, lineHeight: 0 }}>
        {swatch(theme, 26)}
      </button>
      <span style={{ width: 1, height: 29, background: '#F3F3F4' }} />
      {others.map((th) => (
        <button
          key={th}
          type="button"
          onClick={() => onChange(th)}
          aria-label={`switch to ${th} theme`}
          className="transition-transform hover:scale-110"
          style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: 0, lineHeight: 0 }}
        >
          {swatch(th, 24)}
        </button>
      ))}
    </div>
  );
}

export function AuthCanvas({ renderDesktopForm, renderMobileForm, renderLandscapeForm, initialTheme = 'orange' }: AuthCanvasProps) {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const colors = THEME[theme];
  // 手机横屏舞台判定：用设备物理朝向(screen.orientation),不受软键盘压缩视口影响。见 useIsMobileLandscapeStage。
  const isLandscapeStage = useIsMobileLandscapeStage();

  // 将 1920×1080 画布缩放到当前视口；视口比 16:9 更宽时按高度缩放并把多余横向宽度灌入中部（详见 login.tsx 原注释）。
  const [{ scale, shift }, setLayout] = useState({ scale: 1, shift: 0 });
  useEffect(() => {
    const update = () => {
      const sW = window.innerWidth / STAGE_W;
      const sH = window.innerHeight / STAGE_H;
      if (sW >= sH) {
        setLayout({ scale: sH, shift: window.innerWidth / sH - STAGE_W });
      } else {
        setLayout({ scale: sW, shift: 0 });
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const pillStyle = (bg: string): React.CSSProperties => ({
    position: 'absolute', top: 28, width: 90, height: 39, borderRadius: 28.5, background: bg,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 14, fontWeight: 400, color: '#000', border: 0, cursor: 'pointer',
  });

  return (
    <>
      {/* 移动端·竖屏：顶部语言切换 / 中间表单（可滚动） / 底部 UGC+教大 logo。lg:hidden 保证 ≥lg 走桌面画布。
          手机横屏另走下方专用 844×390 舞台,故 isLandscapeStage 为真时本竖屏分支 :hidden。
          ⚠️ isLandscapeStage 以设备物理朝向(screen.orientation)判定,而非 CSS (orientation:landscape)/(max-height:500px)：
          真机(微信 X5 等)竖屏弹软键盘会压缩视口令 CSS 误判横屏→本表单被卸载→键盘弹不起。物理朝向不受键盘影响,详见 useIsMobileLandscapeStage。 */}
      <div className={`lg:hidden fixed inset-0 overflow-y-auto ${isLandscapeStage ? "hidden" : "flex flex-col"}`} style={{ backgroundColor: colors.pageBg }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 20px' }}>
          <LangToggle chip={colors.chip} variant="mobile" />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '8px 24px 24px' }}>
          <div style={{ width: '100%', maxWidth: 420 }}>
            {renderMobileForm({ theme, colors })}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 32, padding: '20px 16px 32px' }}>
          <img src="/images/login/ugc-logo.png" alt="University Grants Committee" style={{ height: 28, width: 'auto' }} />
          <img src="/images/login/footer-illustration.svg" alt="The Education University of Hong Kong" style={{ height: 28, width: 'auto' }} />
        </div>
      </div>

      {/* 手机横屏 (设备物理横屏 且 <lg)：独立 844×390 设计稿舞台(共享壳 + 各页横屏表单)。
          lg:hidden! 确保 ≥lg(即便矮屏)仍走桌面画布,与竖屏移动流/桌面画布三分支互斥。
          isLandscapeStage 见上:以物理朝向判定,防止竖屏软键盘压缩视口误触本横屏舞台。 */}
      {renderLandscapeForm && (
        <div className={`lg:hidden! fixed inset-0 items-center justify-center overflow-hidden ${isLandscapeStage ? "flex" : "hidden"}`} style={{ backgroundColor: colors.pageBg }}>
          <MobileLandscapeAuthShell theme={theme} setTheme={setTheme} colors={colors}>
            {renderLandscapeForm({ theme, colors })}
          </MobileLandscapeAuthShell>
        </div>
      )}

      {/* 桌面端 (≥lg)：1920×1080 缩放画布 */}
      <div className="hidden lg:flex fixed inset-0 items-center justify-center overflow-hidden" style={{ backgroundColor: colors.pageBg }}>
        <div style={{ width: STAGE_W + shift, height: STAGE_H, transform: `scale(${scale})`, transformOrigin: 'center', position: 'relative', flex: '0 0 auto' }}>
          {/* 左侧 Welcome 卡片 */}
          <Welcome theme={theme} colors={colors} />

          {/* 右侧 1920 宽容器：超宽屏下整体右移 shift */}
          <div style={{ position: 'absolute', left: shift, top: 0, width: STAGE_W, height: STAGE_H }}>
            {/* 顶部胶囊：關於我們 / 活動介紹 */}
            <button type="button" style={{ ...pillStyle(colors.pillAbout), left: 1041 }} className="hover:brightness-[0.97]">
              {t('login.about-us')}
            </button>
            <CompetitionRulesDialog>
              <button type="button" style={{ ...pillStyle(colors.pillActivity), left: 1149 }} className="hover:brightness-[0.97]">
                {t('login.activities')}
              </button>
            </CompetitionRulesDialog>

            {/* 主题切换器 + 语言切换 */}
            <ThemeSwitcher theme={theme} onChange={setTheme} />
            <LangToggle chip={colors.chip} />

            {/* 右侧表单内容（由各页自行定位） */}
            {renderDesktopForm({ theme, colors })}

            {/* 右下机构 Logo */}
            <div style={{ position: 'absolute', left: 1513, top: 1019, display: 'flex', alignItems: 'center', gap: 41 }}>
              <img src="/images/login/ugc-logo.png" alt="University Grants Committee" style={{ height: 33, width: 'auto' }} />
              <img src="/images/login/footer-illustration.svg" alt="The Education University of Hong Kong" style={{ height: 33, width: 'auto' }} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AuthCanvas;
